import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { OverlayPanelModule } from 'primeng/overlaypanel';

import { DomainMultipleSelectorComponent } from '../../domain-multiple-selector/domain-multiple-selector.component';
import { HostedSummary } from '../../../models/billing.model';
import { BillingService } from '../../../services/billing.service';
import { NotificationsService } from '../../../services/notifications.service';
import { DomainsService } from '../../../services/domains.service';
import { NetsapiensDomain } from '@voice/utils';
import { saveAs } from 'file-saver';
import { LookupFilterService } from '../shared/services/lookup-filter.service';
import { FilterFieldOption, ColumnConfig } from '../shared/types/lookup-screen.types';
import { HostedSummaryTourService } from './hosted-summary-tour.service';

export class DomainData {
  domain!: string;
  summaries!: HostedSummary[];
  records!: HostedSummary[];
  filteredRecords!: HostedSummary[];
  searchTerm: string = '';
  selectedFilterFields: string[] = ['all'];
  columnVisibility: Record<string, boolean> = {};
}

@Component({
  selector: 'app-hosted-summary',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    CheckboxModule,
    FormsModule,
    ButtonModule,
    PanelModule,
    TooltipModule,
    InputTextModule,
    MultiSelectModule,
    OverlayPanelModule,
    DomainMultipleSelectorComponent,
  ],
  templateUrl: './hosted-summary.component.html',
  styleUrl: './hosted-summary.component.scss',
})
export class HostedSummaryComponent implements OnInit {
  private readonly tourService = inject(HostedSummaryTourService);

  domains: NetsapiensDomain[] = [];

  collapsed = false;
  requesting = false;
  domainSummaries?: DomainData[];
  selectedDomains?: string[];

  readonly availableFilterFields: FilterFieldOption[] = [
    { label: 'All Fields', value: 'all' },
    { label: 'Site', value: 'SITE' },
    { label: 'Scope', value: 'SCOPE' },
    { label: 'Service Type', value: 'SERVICE_TYPE' },
    { label: 'Quantity', value: 'QUANTITY' },
  ];

  readonly columns: ColumnConfig[] = [
    { key: 'SITE', label: 'Site', visible: true },
    { key: 'SCOPE', label: 'Scope', visible: true },
    { key: 'SERVICE_TYPE', label: 'Service type', visible: true },
    { key: 'QUANTITY', label: 'Quantity', visible: true },
  ];

  constructor(
    private readonly billingService: BillingService,
    private readonly notificationService: NotificationsService,
    private readonly domainsService: DomainsService,
    private readonly filterService: LookupFilterService
  ) {}

  ngOnInit(): void {
    this.domainsService.getDomains().subscribe({
      next: (data) => {
        this.domains = (data ?? [])
          .slice()
          .sort((a, b) => a.domain.localeCompare(b.domain));
      },
      error: (err) => this.notificationService.error(err),
    });
  }

  process(domains: string[] | null) {
    if (domains) {
      this.requesting = true;
      this.selectedDomains = domains;
      this.billingService.getHostedSummaries(domains).subscribe({
        next: (data) => {
          this.domainSummaries = domains.map((d) => {
            const domainData: DomainData = {
              domain: d,
              summaries: data.rows.filter(
                (r) => r.DOMAIN === d && r.SCOPE === ''
              ),
              records: data.rows.filter((r) => r.DOMAIN === d && r.SCOPE !== ''),
              filteredRecords: [],
              searchTerm: '',
              selectedFilterFields: ['all'],
              columnVisibility: this.initializeColumnVisibility(),
            };
            domainData.filteredRecords = domainData.records;
            return domainData;
          });
          this.requesting = false;
          this.collapsed = true;
        },
        error: (err) => this.notificationService.error(err),
      });
    } else {
      this.domainSummaries = undefined;
      this.collapsed = false;
    }
  }

  initializeColumnVisibility(): Record<string, boolean> {
    const visibility: Record<string, boolean> = {};
    this.columns.forEach((col) => {
      visibility[col.key] = col.visible;
    });
    return visibility;
  }

  search(domainData: DomainData): void {
    this.applyFilters(domainData);
  }

  clearSearch(domainData: DomainData): void {
    domainData.searchTerm = '';
    domainData.selectedFilterFields = ['all'];
    this.applyFilters(domainData);
  }

  applyFilters(domainData: DomainData): void {
    domainData.filteredRecords = this.filterService.filterItems(
      domainData.records,
      domainData.searchTerm,
      domainData.selectedFilterFields,
      (item: HostedSummary, term: string, field: string) => {
        const value = item[field as keyof HostedSummary]?.toString().toLowerCase() || '';
        return value.includes(term.toLowerCase());
      }
    );
  }

  isColumnVisible(domainData: DomainData, column: string): boolean {
    return domainData.columnVisibility[column] ?? true;
  }

  getVisibleColumnsCount(domainData: DomainData): number {
    return Object.values(domainData.columnVisibility).filter((v) => v).length;
  }

  startTour(): void {
    this.tourService.start();
  }

  download(): void {
    if (this.selectedDomains) {
      this.billingService
        .getHostedSummariesAsCSV(this.selectedDomains)
        .subscribe({
          next: (buffer: string) => {
            const data: Blob = new Blob([buffer], {
              type: 'text/csv;charset=utf-8',
            });
            saveAs(data, 'hosted-billing-summary.csv');
            this.notificationService
              .success('File saved. Please, check your downloads folder')
              .then();
          },
          error: (err) => this.notificationService.error(err),
        });
    }
  }
}
